import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Award, LayoutGrid, Pencil, Plus, Table2, Trash2 } from 'lucide-react';
import { Heading } from '../../layout';
import { FormDrawer } from '../../forms';
import { useUIColors } from '../../../hooks/useTheme';
import { useSectionAccess } from '../../../hooks/useSectionAccess';
import { additivesCatalogAPI } from '../../../services/additivesCatalogAPI';
import ProductionDataTable from '../../placeholders/ProductionDataTable';
import {
  STANDARD_APPLICATIONS,
  STANDARD_KIND_OPTIONS,
  collectStandardApplications,
  collectStandardProperties,
  collectStandardSectors,
  normalizeStandardKind,
  normalizeStandardSector,
} from './productApplications';

const tableCell = (children, align = 'left') => (
  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: align }}>{children}</td>
);

const KIND_TONE = {
  Norma: { bg: '#e0f2fe', color: '#0369a1', darkBg: '#1e3a5f', darkColor: '#7dd3fc' },
  Certificación: { bg: '#dcfce7', color: '#166534', darkBg: '#14532d', darkColor: '#86efac' },
  Método: { bg: '#fef9c3', color: '#a16207', darkBg: '#422006', darkColor: '#fde047' },
  'Marco regulatorio': { bg: '#ffedd5', color: '#c2410c', darkBg: '#431407', darkColor: '#fdba74' },
};

const VIEW_STORAGE_KEY = 'sqmt_standards_view';

const emptyStandard = () => ({
  kind: 'Norma',
  code: '',
  title: '',
  summary: '',
  sectors: [],
  applications: [],
  properties: [],
});

const chipStyle = (active, ui) => ({
  height: 30,
  padding: '0 12px',
  borderRadius: 999,
  border: `1px solid ${active ? ui.interactive || '#16a34a' : ui.border}`,
  background: active ? ui.interactive || '#16a34a' : 'transparent',
  color: active ? ui.textOnAccent || '#fff' : ui.text,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
});

const ChipPicker = ({
  label,
  value = [],
  options = [],
  onChange,
  ui,
  allowCustom = false,
  placeholder = 'Otro…',
}) => {
  const [draft, setDraft] = useState('');
  const selected = Array.isArray(value) ? value : [];

  const toggle = (item) => {
    onChange(selected.includes(item) ? selected.filter((entry) => entry !== item) : [...selected, item]);
  };

  const addDraft = () => {
    const next = draft.trim();
    if (!next) return;
    if (!selected.includes(next)) onChange([...selected, next]);
    setDraft('');
  };

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: ui.text }}>{label}</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {options.map((item) => {
          const active = selected.includes(item);
          return (
            <button key={item} type="button" onClick={() => toggle(item)} style={chipStyle(active, ui)}>
              {item}
            </button>
          );
        })}
      </div>
      {allowCustom ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addDraft();
              }
            }}
            placeholder={placeholder}
            style={{
              flex: 1,
              height: 34,
              padding: '0 10px',
              borderRadius: 8,
              border: `1px solid ${ui.inputBorder || ui.border}`,
              background: ui.inputBg || ui.panel,
              color: ui.inputText || ui.text,
              fontSize: 13,
            }}
          />
          <button type="button" onClick={addDraft} style={chipStyle(false, ui)}>
            Agregar
          </button>
        </div>
      ) : null}
    </div>
  );
};

const renderChipGroup = (values, ui, emptyLabel) => (
  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
    {(values || []).length ? (
      values.map((value) => (
        <span
          key={value}
          style={{
            fontSize: 11,
            fontWeight: 600,
            padding: '3px 8px',
            borderRadius: 999,
            border: `1px solid ${ui.border}`,
            color: ui.text,
          }}
        >
          {value}
        </span>
      ))
    ) : (
      <span style={{ fontSize: 12, color: ui.muted }}>{emptyLabel}</span>
    )}
  </div>
);

const normalizeItem = (item = {}) => {
  const sectors = Array.isArray(item.sectors)
    ? item.sectors.map(normalizeStandardSector).filter(Boolean)
    : [];
  return {
    ...item,
    kind: normalizeStandardKind(item.kind),
    sectors,
    applications: Array.isArray(item.applications) ? item.applications.filter(Boolean) : [],
    properties: Array.isArray(item.properties) ? item.properties.filter(Boolean) : [],
  };
};

const StandardsWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const access = useSectionAccess();
  const [kind, setKind] = useState('Todas');
  const [sector, setSector] = useState('Todas');
  const [application, setApplication] = useState('Todas');
  const [property, setProperty] = useState('Todas');
  const [view, setView] = useState(() => {
    try {
      return window.localStorage.getItem(VIEW_STORAGE_KEY) === 'table' ? 'table' : 'cards';
    } catch {
      return 'cards';
    }
  });
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyStandard());
  const [editingId, setEditingId] = useState(null);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const rows = await additivesCatalogAPI.listStandards();
      setItems(Array.isArray(rows) ? rows.map(normalizeItem) : []);
    } catch (err) {
      setItems([]);
      setError(err?.message || 'No se pudieron cargar las normas.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, view);
    } catch {
      /* ignore */
    }
  }, [view]);

  const kinds = useMemo(() => {
    const extras = items
      .map((item) => item.kind)
      .filter((value) => value && !STANDARD_KIND_OPTIONS.includes(value) && value !== 'Propiedad intelectual');
    return ['Todas', ...STANDARD_KIND_OPTIONS, ...Array.from(new Set(extras))];
  }, [items]);

  const sectorOptions = useMemo(() => collectStandardSectors(items), [items]);
  const applicationOptions = useMemo(() => collectStandardApplications(items), [items]);
  const propertyOptions = useMemo(() => collectStandardProperties(items), [items]);

  const visible = useMemo(
    () =>
      items.filter((item) => {
        if (item.kind === 'Propiedad intelectual') return false;
        if (kind !== 'Todas' && item.kind !== kind) return false;
        if (sector !== 'Todas' && !(item.sectors || []).includes(sector)) return false;
        if (application !== 'Todas' && !(item.applications || []).includes(application)) return false;
        if (property !== 'Todas' && !(item.properties || []).includes(property)) return false;
        return true;
      }),
    [items, kind, sector, application, property]
  );

  const tableRows = useMemo(
    () =>
      visible.map((item) => ({
        ...item,
        sectorsLabel: (item.sectors || []).join(' '),
        applicationsLabel: (item.applications || []).join(' '),
        propertiesLabel: (item.properties || []).join(' '),
      })),
    [visible]
  );

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyStandard());
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      kind: normalizeStandardKind(item.kind),
      code: item.code || '',
      title: item.title || '',
      summary: item.summary || '',
      sectors: Array.isArray(item.sectors) ? item.sectors.map(normalizeStandardSector).filter(Boolean) : [],
      applications: Array.isArray(item.applications) ? item.applications : [],
      properties: Array.isArray(item.properties) ? item.properties : [],
    });
    setFormError('');
    setFormOpen(true);
  };

  const saveForm = async () => {
    try {
      setIsSaving(true);
      setFormError('');
      const payload = {
        ...form,
        kind: normalizeStandardKind(form.kind),
        sectors: (form.sectors || []).map(normalizeStandardSector).filter(Boolean),
      };
      if (editingId) {
        await additivesCatalogAPI.updateStandard(editingId, payload);
      } else {
        await additivesCatalogAPI.createStandard(payload);
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      setFormError(err?.message || 'No se pudo guardar la ficha.');
    } finally {
      setIsSaving(false);
    }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`¿Eliminar “${item.code || item.title}”?`)) return;
    try {
      await additivesCatalogAPI.removeStandard(item.id);
      await load();
    } catch (err) {
      window.alert(err?.message || 'No se pudo eliminar la ficha.');
    }
  };

  const primaryBtn = {
    height: 34,
    borderRadius: 8,
    border: 'none',
    background: ui.interactive || '#16a34a',
    color: ui.textOnAccent || '#fff',
    padding: '0 12px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };
  const ghostBtn = {
    height: 28,
    borderRadius: 8,
    border: `1px solid ${ui.border}`,
    background: 'transparent',
    color: ui.text,
    padding: '0 8px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  };
  const viewBtn = (active) => ({
    width: 34,
    height: 34,
    borderRadius: 8,
    border: `1px solid ${active ? ui.interactive || '#16a34a' : ui.border}`,
    background: active ? ui.interactive || '#16a34a' : 'transparent',
    color: active ? ui.textOnAccent || '#fff' : ui.text,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  const filterRow = (label, current, options, onChange) => (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: ui.muted, minWidth: 78 }}>{label}</span>
      {['Todas', ...options].map((item) => (
        <button key={`${label}-${item}`} type="button" onClick={() => onChange(item)} style={chipStyle(current === item, ui)}>
          {item}
        </button>
      ))}
    </div>
  );

  const formSections = [
    {
      key: 'main',
      title: 'Ficha',
      defaultOpen: true,
      fields: [
        {
          key: 'kind',
          label: 'Tipo',
          type: 'select',
          options: STANDARD_KIND_OPTIONS.map((value) => ({ value, label: value })),
        },
        { key: 'code', label: 'Código' },
        { key: 'title', label: 'Título', fullWidth: true },
        { key: 'summary', label: 'Descripción', type: 'textarea', rows: 4, fullWidth: true },
        {
          key: 'sectors',
          label: 'Sector',
          type: 'custom',
          fullWidth: true,
          render: ({ value, onChange }) => (
            <ChipPicker
              label="Sector"
              value={value}
              onChange={onChange}
              options={collectStandardSectors([{ sectors: value }, ...items])}
              ui={ui}
            />
          ),
        },
        {
          key: 'applications',
          label: 'Aplicación',
          type: 'custom',
          fullWidth: true,
          render: ({ value, onChange }) => (
            <ChipPicker
              label="Aplicación"
              value={value}
              onChange={onChange}
              options={STANDARD_APPLICATIONS}
              ui={ui}
            />
          ),
        },
        {
          key: 'properties',
          label: 'Propiedad',
          type: 'custom',
          fullWidth: true,
          render: ({ value, onChange }) => (
            <ChipPicker
              label="Propiedad"
              value={value}
              onChange={onChange}
              options={collectStandardProperties([{ properties: value }, ...items])}
              ui={ui}
              allowCustom
              placeholder="Otra propiedad…"
            />
          ),
        },
      ],
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div>
        <Heading level={2} isDarkMode={isDarkMode}>
          <Award size={22} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Normas y certificaciones
        </Heading>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: ui.muted }}>
          Clasifica cada ficha por tipo, sector, aplicación y propiedad.
        </p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {kinds.map((item) => (
            <button key={item} type="button" onClick={() => setKind(item)} style={chipStyle(kind === item, ui)}>
              {item}
            </button>
          ))}
        </div>
        <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          <div style={{ display: 'inline-flex', gap: 4 }}>
            <button type="button" title="Vista cards" onClick={() => setView('cards')} style={viewBtn(view === 'cards')}>
              <LayoutGrid size={15} />
            </button>
            <button type="button" title="Vista tabla" onClick={() => setView('table')} style={viewBtn(view === 'table')}>
              <Table2 size={15} />
            </button>
          </div>
          {view === 'cards' && access.canCreate ? (
            <button type="button" onClick={openCreate} style={primaryBtn}>
              <Plus size={14} />
              Nueva ficha
            </button>
          ) : null}
        </div>
      </div>

      {filterRow('Sector', sector, sectorOptions, setSector)}
      {filterRow('Aplicación', application, applicationOptions, setApplication)}
      {filterRow('Propiedad', property, propertyOptions, setProperty)}

      {error ? <div style={{ fontSize: 13, color: '#dc2626' }}>{error}</div> : null}
      {isLoading ? <div style={{ fontSize: 13, color: ui.muted }}>Cargando fichas…</div> : null}

      {!isLoading && view === 'cards' ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 14,
          }}
        >
          {visible.map((item) => {
            const tone = KIND_TONE[item.kind] || KIND_TONE.Norma;
            return (
              <article
                key={item.id}
                style={{
                  border: `1px solid ${ui.border}`,
                  borderRadius: 14,
                  background: ui.panel,
                  padding: 16,
                  display: 'grid',
                  gap: 10,
                  minWidth: 0,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: ui.text }}>{item.code}</div>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '4px 8px',
                      borderRadius: 999,
                      background: isDarkMode ? tone.darkBg : tone.bg,
                      color: isDarkMode ? tone.darkColor : tone.color,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.kind}
                  </span>
                </div>
                <h3 style={{ margin: 0, fontSize: 15, color: ui.text, lineHeight: 1.35 }}>{item.title}</h3>
                <div style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: ui.muted }}>Sector</div>
                  {renderChipGroup(item.sectors, ui, 'Sin sector')}
                  <div style={{ fontSize: 11, fontWeight: 700, color: ui.muted }}>Aplicación</div>
                  {renderChipGroup(item.applications, ui, 'Sin aplicación')}
                  <div style={{ fontSize: 11, fontWeight: 700, color: ui.muted }}>Propiedad</div>
                  {renderChipGroup(item.properties, ui, 'Sin propiedad')}
                </div>
                {item.summary ? (
                  <p style={{ margin: 0, fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>{item.summary}</p>
                ) : null}
                {access.canUpdate || access.canDelete ? (
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    {access.canUpdate ? (
                      <button type="button" onClick={() => openEdit(item)} style={ghostBtn} title="Editar">
                        <Pencil size={13} />
                      </button>
                    ) : null}
                    {access.canDelete ? (
                      <button type="button" onClick={() => removeItem(item)} style={ghostBtn} title="Eliminar">
                        <Trash2 size={13} />
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : null}

      {!isLoading && view === 'table' ? (
        <ProductionDataTable
          title="Normas y certificaciones"
          subtitle={`${visible.length} ficha${visible.length === 1 ? '' : 's'}`}
          rows={tableRows}
          columns={[
            { key: 'Código', sortKey: 'code' },
            { key: 'Título', sortKey: 'title' },
            { key: 'Tipo', sortKey: 'kind' },
            { key: 'Sector', sortKey: 'sectorsLabel' },
            { key: 'Aplicación', sortKey: 'applicationsLabel' },
            { key: 'Propiedad', sortKey: 'propertiesLabel' },
            { key: '', sortKey: '' },
          ]}
          defaultSortKey="code"
          searchPlaceholder="Buscar código, título, sector o propiedad…"
          onRowClick={access.canUpdate ? openEdit : undefined}
          action={
            access.canCreate ? (
              <button type="button" onClick={openCreate} style={primaryBtn}>
                <Plus size={14} />
                Nueva ficha
              </button>
            ) : null
          }
          renderCells={(item) => {
            const tone = KIND_TONE[item.kind] || KIND_TONE.Norma;
            return (
              <>
                {tableCell(<strong>{item.code || '—'}</strong>)}
                {tableCell(item.title || '—')}
                {tableCell(
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '3px 8px',
                      borderRadius: 999,
                      background: isDarkMode ? tone.darkBg : tone.bg,
                      color: isDarkMode ? tone.darkColor : tone.color,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.kind}
                  </span>
                )}
                {tableCell(renderChipGroup(item.sectors, ui, '—'))}
                {tableCell(renderChipGroup(item.applications, ui, '—'))}
                {tableCell(renderChipGroup(item.properties, ui, '—'))}
                {tableCell(
                  <div style={{ display: 'inline-flex', gap: 6 }}>
                    {access.canUpdate ? (
                      <button
                        type="button"
                        title="Editar"
                        onClick={(event) => {
                          event.stopPropagation();
                          openEdit(item);
                        }}
                        style={ghostBtn}
                      >
                        <Pencil size={13} />
                      </button>
                    ) : null}
                    {access.canDelete ? (
                      <button
                        type="button"
                        title="Eliminar"
                        onClick={(event) => {
                          event.stopPropagation();
                          removeItem(item);
                        }}
                        style={ghostBtn}
                      >
                        <Trash2 size={13} />
                      </button>
                    ) : null}
                  </div>,
                  'right'
                )}
              </>
            );
          }}
        />
      ) : null}

      {!isLoading && view === 'cards' && visible.length === 0 ? (
        <div style={{ fontSize: 13, color: ui.muted, padding: 12 }}>No hay fichas. Crea la primera con “Nueva ficha”.</div>
      ) : null}

      <FormDrawer
        isOpen={formOpen}
        onClose={() => !isSaving && setFormOpen(false)}
        onSave={saveForm}
        isSaving={isSaving}
        isDarkMode={isDarkMode}
        title={editingId ? 'Editar ficha' : 'Nueva ficha'}
        subtitle="Normas y certificaciones"
        sections={formSections}
        formData={form}
        onChange={(key, value) => setForm((prev) => ({ ...prev, [key]: value }))}
        formError={formError}
        saveLabel={editingId ? 'Guardar' : 'Crear'}
        readOnly={editingId ? !access.canUpdate : !access.canCreate}
        width={560}
      />
    </div>
  );
};

export default StandardsWorkspace;
